import { Fragment, useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import TableToolbar from "./TableToolbar";
import ColumnFilter from "./ColumnFilter";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";

const LightTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#1976d2",
		color: "white",
		boxShadow: theme.shadows[1],
		fontSize: 11,
	},
}));
const getColumns = (dataType = "ball") => {
	const columns = [
		{
			field: "name",
			headerName: "Name",
			flex: 1,
			disableColumnMenu: true,
			renderCell: ({ value, row }) => {
				return (
					<Box fullWidth display="flex">
						<Typography sx={{ color: row.back_order ? "red" : "black" }}>
							{value}
						</Typography>
						<LightTooltip
							title={
								<Fragment>
									{value === "Gamer" ? (
										<Typography sx={{ fontWeight: "bold" }}>
											Gamer refers to the initial club that the customer brought
											in
										</Typography>
									) : (
										<>
											{row.back_order ? (
												<Typography sx={{ fontWeight: "bold" }}>
													* This item is on back order.
												</Typography>
											) : null}
											<Typography sx={{ fontWeight: "bold" }}>
												Product Code: {row.code}
											</Typography>
											<Typography sx={{ fontWeight: "bold" }}>
												Price: {" $" + row.price}
											</Typography>
											<Typography sx={{ fontWeight: "bold" }}>
												Full Name: {row.name}
											</Typography>
										</>
									)}
								</Fragment>
							}
							placement="top"
						>
							<InfoOutlinedIcon htmlColor="#003F5E" />
						</LightTooltip>
					</Box>
				);
			},
		},
	];

	// Add family column for all data types
	columns.push({
		field: "family",
		headerName: "Family",
		flex: 0.5,
	});

	// Add manufacturer column
	columns.push({
		field: "manufacturer",
		headerName: "Manufacturer",
		flex: 0.5,
	});

	return columns;
};

//Need to escape special regex characters like '.'
function escapeRegExp(value) {
	return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

const CustomNoRowsOverlay = ({ insufficientData }) => {
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100%',
				padding: 3,
			}}
		>
			<Typography variant="h6" sx={{ color: "#666", fontWeight: "bold" }}>
				{insufficientData
					? "Insufficient data to generate recommendations for this club subtype."
					: "No rows"}
			</Typography>
		</Box>
	);
};

const Table = ({
	rows,
	onSelection,
	famFilterOpts,
	manFilterOpts,
	onShowAll,
	currentSelection,
	dataType = 'ball',
	insufficientData = false,
}) => {
	const [searchText, setSearchText] = useState("");
	const [famFilterVal, setFamFilterVal] = useState([...famFilterOpts]);
	const [manFilterVal, setManFilterVal] = useState([...manFilterOpts]);
	const [displayRows, setDisplayRows] = useState([...rows]);
	const [pageSize, setPageSize] = useState(5);
	
	const columns = getColumns(dataType);
	const primaryField = 'name'; // All data types now use 'name'
	const secondaryField = dataType === 'head' || dataType === 'shaft' ? 'family' : 'manufacturer';

	useEffect(() => {
		setFamFilterVal([...famFilterOpts]);
		setManFilterVal([...manFilterOpts]);
		setDisplayRows([...rows]);
	}, [famFilterOpts, manFilterOpts, rows]);
	const handleFilterChange = (id, value) => {
		var rows;
		switch (id) {
			case "search":
				setSearchText(value);
				rows = filterRowsBySelections(famFilterVal, manFilterVal);
				filterRowsByText(value, rows);
				break;
			case "familyFilter":
				setFamFilterVal([...value]);
				rows = filterRowsBySelections([...value], manFilterVal);
				filterRowsByText(searchText, rows);
				break;
			case "manFilter":
				setManFilterVal([...value]);
				rows = filterRowsBySelections(famFilterVal, [...value]);
				filterRowsByText(searchText, rows);
				break;
			default:
		}
	};
	const filterRowsByText = (text, rows) => {
		const searchRegex = new RegExp(escapeRegExp(text), "i");
		const filteredRows = rows.filter((row) => {
			return Object.keys(row).some((field) => {
				if (row[field]) {
					return searchRegex.test(row[field].toString());
				}
				return false;
			});
		});
		setDisplayRows(filteredRows);
	};

	const filterRowsBySelections = (familyVal, manVal) => {
		if (familyVal.length === 0 && manVal.length === 0) {
			return rows;
		}
		if (familyVal.length === 0) {
			return rows.filter((row) => manVal.includes(row.manufacturer));
		}
		if (manVal.length === 0) {
			return rows.filter((row) => familyVal.includes(row.family));
		}
		return rows.filter((row) => {
			return (
				familyVal.includes(row.family) && manVal.includes(row.manufacturer)
			);
		});
	};


	return (
		<DataGrid
			sx={{
				"& .MuiDataGrid-row": { cursor: "pointer" },
				"& .MuiDataGrid-virtualScroller": {
					overflow: "hidden",
				},
				"& .MuiDataGrid-cell:focus": {
					outline: "none",
				},
				"& .MuiDataGrid-columnHeader:focus": {
					outline: "none",
				},
				"& .MuiDataGrid-columnHeader:focus-within": {
					outline: "none",
				},
			}}
			disableColumnFilter
			rows={displayRows}
			columns={columns}
			rowsPerPageOptions={[5, 10, 20]}
			pageSize={pageSize}
			onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
			autoHeight={true}
			components={{
				Toolbar: TableToolbar,
				ColumnMenu: ColumnFilter,
				NoRowsOverlay: () => <CustomNoRowsOverlay insufficientData={insufficientData} />
			}}
			componentsProps={{
				toolbar: {
					searchValue: searchText,
					onChange: handleFilterChange,
					onShowAll: onShowAll,
				},
				columnMenu: {
					filters: {
						Family: {
							filterOptions: famFilterOpts,
							filterValue: famFilterVal,
							id: "familyFilter",
						},
						Manufacturer: {
							filterOptions: manFilterOpts,
							filterValue: manFilterVal,
							id: "manFilter",
						},
					},
					onApply: handleFilterChange,
				},
			}}
			getRowId={(r) => r.id}
			hideFooterSelectedRowCount
			onSelectionModelChange={onSelection}
			selectionModel={currentSelection?.id || []}
		/>
	);
};

export default Table;
